import { useState, useEffect, useCallback } from "react";
import { MapPin, Repeat, SquareParking } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import { errorText } from "@/lib/api";
import { PageHead, Panel, Tag, EmptyState, Button, DataTable, Td, Label, Field, Select, Notice, resTone, money } from "@/webapp/fleet-owners/ui";

const addDays = (d, n) => new Date(new Date(d).getTime() + n * 86400000).toISOString().slice(0, 10);

function BookForm({ listing, data, preset, onDone, onCancel }) {
  const [form, setForm] = useState({ channel: "B2C", nights: 1, start_date: new Date().toISOString().slice(0, 10), vehicle_id: data.vehicles[0]?.id || "", driver_id: "", repeat: 1, ...preset });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const weeks = form.channel === "B2C" ? Math.min(4, Math.max(1, Number(form.repeat) || 1)) : 1;
  const each = form.channel === "B2C" ? listing.rate_b2c * Math.max(1, Number(form.nights) || 1) : listing.rate_b2b;
  const book = async () => {
    setBusy(true); setErr("");
    const made = [];
    try {
      for (let w = 0; w < weeks; w++) made.push(await foApi.reserve({ channel: form.channel, nights: Number(form.nights), vehicle_id: form.vehicle_id, driver_id: form.driver_id, listing_id: listing.listing_id, start_date: addDays(form.start_date, w * 7) }));
      onDone(made);
    } catch (e) {
      setErr(`${made.length ? `${made.length} of ${weeks} booked. ` : ""}${errorText(e)}`);
      if (made.length) onDone(made, true);
    }
    setBusy(false);
  };
  if (!data.vehicles.length) return <div className="mt-4"><Notice testid="fo-book-no-vehicle">Add a vehicle first. Every booking is tied to a unit and its plate.</Notice></div>;
  return (
    <div className="mt-4 space-y-4 border-t border-slate-100 pt-4" data-testid="fo-book-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <div><Label>Type</Label><Select data-testid="fo-book-channel" value={form.channel} onChange={set("channel")}><option value="B2C">Overnight</option><option value="B2B">Monthly (30 days)</option></Select></div>
        {form.channel === "B2C" && <div><Label>Nights</Label><Field data-testid="fo-book-nights" type="number" min={1} max={30} value={form.nights} onChange={set("nights")} /></div>}
        <div><Label>Start</Label><Field data-testid="fo-book-start" type="date" value={form.start_date} onChange={set("start_date")} /></div>
        <div><Label>Vehicle</Label><Select data-testid="fo-book-vehicle" value={form.vehicle_id} onChange={set("vehicle_id")}>{data.vehicles.map((v) => <option key={v.id} value={v.id}>{v.unit}{v.plate ? ` · ${v.plate}` : " · no plate"}</option>)}</Select></div>
        <div><Label>Driver</Label><Select data-testid="fo-book-driver" value={form.driver_id} onChange={set("driver_id")}><option value="">Unassigned</option>{data.drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></div>
        {form.channel === "B2C" && <div><Label>Repeat weekly</Label><Select data-testid="fo-book-repeat" value={form.repeat} onChange={set("repeat")}>{[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n === 1 ? "Once" : `${n} weeks`}</option>)}</Select></div>}
      </div>
      {err && <Notice testid="fo-book-error">{err}</Notice>}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" testid="fo-book-confirm" onClick={book} disabled={busy || !form.vehicle_id}>{busy ? "Booking…" : `Confirm · ${money(each * weeks)}`}</Button>
        <Button variant="ghost" testid="fo-book-cancel" onClick={onCancel}>Cancel</Button>
        {weeks > 1 && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Repeat className="h-3.5 w-3.5" />{weeks} bookings, one per week</span>}
      </div>
    </div>
  );
}

function Listing({ l, data, open, setOpen, preset, onDone }) {
  return (
    <Panel testid={`fo-listing-${l.listing_id}`} className="p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-[#FF5722]">{l.operator}</p>
          <p className="mt-1 font-display text-lg font-semibold text-[#0A234A]">{l.site_name} yard</p>
          <p className="mt-0.5 inline-flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{l.site_address}{l.access ? ` · ${l.access}` : ""}</p>
          <p className="mt-1 text-xs text-slate-400">{l.vehicles} · available <span className="font-mono">{l.start_date} → {l.end_date}</span></p>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className="font-mono text-sm font-semibold text-[#0A234A]">{money(l.rate_b2c)}<span className="font-sans text-xs font-normal text-slate-400"> /night</span></p>
            <p className="font-mono text-sm font-semibold text-[#0A234A]">{money(l.rate_b2b)}<span className="font-sans text-xs font-normal text-slate-400"> /month</span></p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Tag tone="ok">{l.open_stalls} open</Tag>
            {open !== l.listing_id && <Button variant="primary" testid={`fo-listing-book-${l.listing_id}`} onClick={() => setOpen(l.listing_id)}>Book</Button>}
          </div>
        </div>
      </div>
      {open === l.listing_id && <BookForm listing={l} data={data} preset={preset} onDone={onDone} onCancel={() => setOpen("")} />}
    </Panel>
  );
}

function Market({ data, reload, preset }) {
  const [listings, setListings] = useState(null);
  const [open, setOpen] = useState("");
  const [msg, setMsg] = useState("");
  const load = useCallback(() => foApi.marketplace().then((r) => setListings(r.listings)).catch(() => setListings([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (preset && listings) { const m = listings.find((l) => l.listing_id === preset.listing_id) || listings.find((l) => l.site === preset.site && l.operator === preset.operator); if (m) setOpen(m.listing_id); } }, [preset, listings]);
  const done = async (made, partial) => { if (!partial) setOpen(""); setMsg(`Booked ${made.map((r) => `${r.ref} (stall ${r.stall})`).join(", ")} · ${money(made.reduce((a, r) => a + r.amount, 0))}`); await Promise.all([load(), reload()]); };
  if (!listings) return <p className="text-sm text-slate-400" data-testid="fo-market-loading">Loading available parking…</p>;
  return (
    <div className="space-y-4">
      {msg && <Notice tone="ok" testid="fo-book-success">{msg}</Notice>}
      {preset && !listings.some((l) => l.listing_id === preset.listing_id || (l.site === preset.site && l.operator === preset.operator)) && <Notice testid="fo-rebook-unavailable">That operator has no open capacity at this site right now. Pick another listing below.</Notice>}
      {listings.length ? listings.map((l) => <Listing key={l.listing_id} l={l} data={data} open={open} setOpen={setOpen} preset={preset} onDone={done} />)
        : <EmptyState icon={SquareParking} testid="fo-market-empty" title="No capacity released right now" body="Site owners release idle stalls into the network. Check back soon." />}
    </div>
  );
}

function Mine({ data, reload, rebook, filter, setFilter }) {
  const cancel = async (r) => { if (window.confirm(`Cancel ${r.ref}?`)) { await foApi.cancel(r.id); await reload(); } };
  const rows = data.reservations.filter((r) => filter === "all" || (filter === "active" ? ["booked", "verified", "onsite"].includes(r.status) : ["completed", "cancelled"].includes(r.status)));
  return (
    <Panel testid="fo-res-panel" title="My bookings" action={
      <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-xs">
        {["active", "history", "all"].map((f) => <button key={f} data-testid={`fo-res-filter-${f}`} onClick={() => setFilter(f)} className={`rounded px-2.5 py-1 font-semibold capitalize transition-colors ${filter === f ? "bg-[#0A234A] text-white" : "text-slate-500 hover:text-[#0A234A]"}`}>{f}</button>)}
      </div>}>
      {rows.length ? (
        <DataTable head={["Ref", "Site", "Stall", "Vehicle", "Dates", "Amount", "Status", ""]} testid="fo-res-table">
          {rows.map((r) => (
            <tr key={r.id} data-testid={`fo-res-row-${r.id}`}>
              <Td mono strong>{r.ref}</Td>
              <Td>{r.operator} · {r.site_name}</Td>
              <Td mono>{r.stall}</Td>
              <Td>{r.vehicle}{r.plate ? <span className="ml-1 font-mono text-xs text-slate-400">{r.plate}</span> : ""}</Td>
              <Td mono>{r.start_date} → {r.end_date}</Td>
              <Td mono strong>{money(r.amount)}</Td>
              <Td><span data-testid={`fo-res-status-${r.id}`}><StatusTag s={r.status} /></span></Td>
              <Td className="text-right">
                {["booked", "verified"].includes(r.status) && <Button variant="ghost" testid={`fo-res-cancel-${r.id}`} onClick={() => cancel(r)}>Cancel</Button>}
                {["completed", "cancelled"].includes(r.status) && <Button variant="ghost" testid={`fo-res-rebook-${r.id}`} onClick={() => rebook(r)}>Book again</Button>}
              </Td>
            </tr>
          ))}
        </DataTable>
      ) : <div className="p-5"><EmptyState testid="fo-res-empty" title="No bookings here" body="Book from available parking to see them here." /></div>}
    </Panel>
  );
}

const StatusTag = ({ s }) => <Tag tone={resTone(s)}>{s}</Tag>;

export default function Parking({ data, reload }) {
  const [view, setView] = useState("market");
  const [filter, setFilter] = useState("active");
  const [preset, setPreset] = useState(null);
  const rebook = (r) => { setPreset({ listing_id: r.listing_id, site: r.site, operator: r.operator, channel: r.channel, nights: r.nights || 1, vehicle_id: r.fleet_vehicle_id || data.vehicles[0]?.id || "", driver_id: r.fleet_driver_id || "" }); setView("market"); };
  const active = data.reservations.filter((r) => ["booked", "verified", "onsite"].includes(r.status)).length;
  return (
    <>
      <PageHead eyebrow="Parking & Bookings" title="Secure truck parking" sub="Book stalls released by site owners across the FleetPods network. Bookings carry your plate so gate cameras check you in automatically.">
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {[["market", "Available"], ["mine", `My bookings · ${active} active`]].map(([id, label]) => <button key={id} data-testid={`fo-res-view-${id}`} onClick={() => setView(id)} className={`rounded px-3 py-1.5 text-sm font-semibold transition-colors ${view === id ? "bg-[#0A234A] text-white" : "text-slate-500 hover:text-[#0A234A]"}`}>{label}</button>)}
        </div>
      </PageHead>
      <div className="fo-rise fo-rise-1">{view === "market" ? <Market data={data} reload={reload} preset={preset} /> : <Mine data={data} reload={reload} rebook={rebook} filter={filter} setFilter={setFilter} />}</div>
    </>
  );
}
